import { useEffect } from 'react';

/**
 * How much of the screen the on-screen keyboard is covering, as a CSS
 * variable on <html>, so a bottom sheet can sit above it.
 *
 * The trap is that this is two different situations wearing the same
 * clothes:
 *
 *   iOS Safari — the keyboard does not resize the page. window.innerHeight
 *     stays the full screen, the sheet stays fixed to the bottom of it,
 *     and the keys cover the sheet. The gap must be padded.
 *
 *   Telegram's WebView — the web view itself is made shorter when the
 *     keyboard opens, and Telegram.WebApp.viewportHeight is that new
 *     height. The page has already lost the space, so padding by the
 *     keyboard's height again takes it twice: on a real iPhone that left
 *     a 266px sheet floating over 650px of dimmed nothing.
 *
 * So the rule is: pad by what the page has NOT already lost, and never
 * by more than a keyboard could plausibly take.
 */

/** Below this, it is browser chrome moving, not a keyboard. */
const NOISE = 60;
/** A keyboard never takes more of the screen than this. */
const MOST = 0.62;

export interface KeyboardView {
  /** The box a fixed element is laid out in. */
  layout: number;
  /** What the person can actually see. */
  visible: number;
  offsetTop: number;
  /** What a bottom sheet has to step over: the part not already lost. */
  inset: number;
  /**
   * A keyboard is up — whether or not anything has to be stepped over.
   * Telegram's WebView shrinks itself, so there is nothing to pad and
   * the sheet still has to be the compact one.
   */
  open: boolean;
  source: 'telegram' | 'visual' | 'none';
  /** Telegram's own numbers, when there are any. */
  tgViewport: number | null;
  tgStable: number | null;
}

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null);

/** Everything the page can know about the keyboard, in one read. */
export function measureKeyboard(): KeyboardView {
  const vv = window.visualViewport;
  const layout = Math.round(window.innerHeight);
  const wa = (window as any).Telegram?.WebApp;
  const tgViewport = num(wa?.viewportHeight);
  const tgStable = num(wa?.viewportStableHeight);
  const vvHeight = num(vv?.height);
  // Telegram's own number first: inside its WebView it is the height the
  // app has been given, keyboard already taken off.
  const visible = Math.round(tgViewport ?? vvHeight ?? layout);
  const offsetTop = Math.max(0, Math.round(vv?.offsetTop ?? 0));
  const raw = layout - visible - offsetTop;
  const inset = raw > NOISE ? Math.min(Math.round(raw), Math.round(layout * MOST)) : 0;
  // What Telegram itself lost between its resting height and now.
  const lost = tgStable !== null && tgViewport !== null ? tgStable - tgViewport : 0;
  return {
    layout, visible, offsetTop, inset, open: inset > 0 || lost > NOISE,
    source: tgViewport !== null ? 'telegram' : vvHeight !== null ? 'visual' : 'none',
    tgViewport, tgStable,
  };
}

/** The focused field, back into view inside its own sheet. */
function keepFieldInView() {
  const el = document.activeElement as HTMLElement | null;
  if (!el || !el.closest?.('.sheet__panel')) return;
  if (!/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

export function useKeyboardInset() {
  useEffect(() => {
    const root = document.documentElement;
    let last = '';
    const apply = () => {
      const { inset, open } = measureKeyboard();
      const now = `${inset}/${open}`;
      if (now === last) return;
      last = now;
      root.style.setProperty('--kb', `${inset}px`);
      // Sheets go compact while the keyboard is up: see styles.css.
      root.dataset.kb = open ? '1' : '0';
      // The keys have just moved; put the field being typed in back where
      // it can be seen, inside whatever is left of the sheet.
      if (open) requestAnimationFrame(keepFieldInView);
    };

    apply();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', apply);
    vv?.addEventListener('scroll', apply);
    window.addEventListener('resize', apply);
    // Telegram says so itself, and says it when nothing else fires.
    const wa = (window as any).Telegram?.WebApp;
    try { wa?.onEvent?.('viewportChanged', apply); } catch { /* older clients */ }
    // Focus and blur are the honest edges of a keyboard on iOS, where the
    // resize can arrive late or not at all.
    const onFocus = () => { setTimeout(apply, 60); setTimeout(apply, 350); };
    document.addEventListener('focusin', onFocus, true);
    document.addEventListener('focusout', onFocus, true);

    return () => {
      vv?.removeEventListener('resize', apply);
      vv?.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
      try { wa?.offEvent?.('viewportChanged', apply); } catch { /* older clients */ }
      document.removeEventListener('focusin', onFocus, true);
      document.removeEventListener('focusout', onFocus, true);
      root.style.removeProperty('--kb');
      delete root.dataset.kb;
    };
  }, []);
}
